import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {StringHelper} from '../../../core/helpers/strings.helper';
import {ValidatorModel, ValidatorsHelper} from '../../../core/helpers/validators.helper';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn} from '@angular/forms';
import {MobileDelegateService} from '../../../core/services/mobile-delegate.service';
import { logger } from '@mpt-fo/common/telemetry';

@Component({
	selector: 'app-kamona-input-field',
	templateUrl: './kamona-input-field.component.html',
	styleUrls: ['./kamona-input-field.component.scss'],
})
export class KamonaInputFieldComponent implements OnInit {
	inputValue: any;
	@Input() input: any;
  readOnlyText = false;
	@Output() emitInput = new EventEmitter();
	@Output() emitInputOnBlur = new EventEmitter();
	readonly IS_VALID_INPUT = 'valid';
	readonly ERROR_CLASS = 'in-valid';
	validationError: boolean;
	placeHolder = ' ';
	label: string;
	datePlaceHolder = ' ';
	inputType = 'text';
	maxInputLength: any;
	isShowInputStateSuffix: boolean;
	loading = true;
	@Input() id: string;
	validInput: boolean;
	useGoogleAutoComplete: boolean;
	canUseOnblurEvent: boolean;
	canUseOnChangeEvent: boolean;

	constructor(private delegateService: MobileDelegateService) {
	}

	ngOnInit(): void {
		this.initialize();
	}

	initialize() {
		this.input = this.input || this.initializeInput();
		this.canUseOnblurEvent = this.input.canUseOnblurEvent;
		this.canUseOnChangeEvent = this.input.canUseOnChangeEvent;
		if (this.input.getInputType() === InputType.number) {
			this.inputType = 'number';
		}
		this.isShowInputStateSuffix = this.input.isShowInputStateSuffix();
		this.maxInputLength = `${this.getInputLength(this.input.getMaxLength(),this.input?.prefix)}`;
		this.label = this.input.getLabel();
		this.id = `kamona-input-field-${this.label}-${this.id}`;
		this.inputValue = this.input.getInput();
    if(this.input.isReadOnlyText()){
      this.readOnlyText = true;
    }
		if (this.inputValue) {
			this.onInputChange(this.inputValue);
		}
		this.loading = false;
	}

	initAutocomplete() {
		const input = document.getElementById(`${this.id}`) as HTMLInputElement;
		const options = {
			componentRestrictions: {country: 'NG'},
			fields: ['address_components', 'geometry', 'icon', 'name'],
			strictBounds: false,
			types: ['address'],
		};

		const autocomplete = new google.maps.places.Autocomplete(input, options);
		autocomplete.addListener('place_changed', () => {
			const place = autocomplete.getPlace();
			if (!place.geometry) {
				console.log('No details available for input:' + input?.value);
			} else {
				this.onInputChange(place?.name);
			}
		});
	}

	initializeInput() {
		return new CustomInput(null, null, true);
	}

	canShowError() {
		return (this.input.isError || this.validationError) && !this.input?.isLoading();
	}

	onBlurInput($event) {
		if (this.canUseOnblurEvent) {
			this.onChangeInput($event);
			this.emitInputOnBlur.emit(this.input);
		}
	}

	onChangeInput($event, defaultValue = null) {
		if (this.canUseOnChangeEvent) {
			let input = defaultValue || $event?.target?.value;
			const isNumberOnly = this.inputType === 'number';
			if (isNumberOnly) {
				input = StringHelper.numbersOnly(input);
				$event.target.value = input;
				$event.target.defaultValue = input;
			}
			this.onInputChange(input);
		}
	}

	onInputChange(input) {
		this.input.setInput(input);
		this.validInput = this.input.validate();
		this.validInput ? this.setValidInputAfterValidation() : this.setErrorAfterValidation();
		this.emitInput.emit(this.input);
	}

	onPaste($event: ClipboardEvent) {
		const clipboardData = $event?.clipboardData;
		const pastedText = clipboardData.getData('text');
		this.onChangeInput($event, pastedText);
	}

	setValidInputAfterValidation() {
		this.placeHolder = ' ';
		this.validationError = false;
	}

	setErrorAfterValidation() {
		this.placeHolder = this.input.getPlaceHolder() || this.input.getLabel();
		this.validationError = true;
	}

	onFocus() {
		document.getElementById(this.label)?.focus();
	}

	getInputLength(currentMaxLength:number, prefix?: string) {
        if (!prefix?.length) {
            return currentMaxLength;
        }
    	const maxInputLength = Number(currentMaxLength);
        const prefixLength = prefix.length;
        const inputLength = maxInputLength - prefixLength;
        return !isNaN(inputLength) ? inputLength : maxInputLength;
    }
}

export class CustomInput {
	static formGroup: FormGroup;
	static canUseImposePolicyPattern: boolean;
	readonly DEFAULT_MAX_LENGTH = 50;
	readonly DEFAULT_MIN_LENGTH = 1;
	readonly DEFAULT_LABEL = 'Field';
	readonly DEFAULT_ERROR_PREFIX = 'Please enter a valid';
	showInputStateSuffix: boolean;
	isError: boolean;
	fb: FormBuilder;
	customValidationFunction: any;
	validator: any;
	canUseOnblurEvent: boolean;
	canUseOnChangeEvent: boolean;
	regex: RegExp;
	showCharCount = false;
	private input: any;
	private required: boolean;
	private maxLength: number;
	private minLength: number;
  private readOnlyText: boolean;
	private placeHolder: string;
	private readonly label: string;
	private inputType: InputType;
	private errorMessage: string;
	private formGroupControl: FormControl;
	private showError = true;
	private loading: boolean;
	private useGoogleAutoComplete: boolean;

	constructor(input, label, required, validator?: any, regex?: RegExp) {
		this.input = input || '';
		this.label = label || this.DEFAULT_LABEL;
		this.placeHolder = this.label;
		this.required = required;
		this.maxLength = this.DEFAULT_MAX_LENGTH;
		this.minLength = this.DEFAULT_MIN_LENGTH;
		this.inputType = InputType.text;
		this.validator = validator;
		this.regex = regex;
		this.canUseOnblurEvent = true;
		this.canUseOnChangeEvent = true;
		this.errorMessage = `${this.DEFAULT_ERROR_PREFIX} ${label}`;
		try {
			this.formGroupControl = this.createFormControl(input, required);
		} catch (err) {
			logger.error('Error creating form', err);
		}
	}

	getUseGoogleAutoComplete() {

		return this.useGoogleAutoComplete;
	}

	setUseGoogleAutoComplete(useAutoComplete) {
		this.useGoogleAutoComplete = useAutoComplete;
	}

	createFormControl(input, required: boolean) {
		if (required) {
			// const defaultRegex: RegExp = /^\s*$/;
			if (this.regex) {
				return new FormControl(input, [this.regexValidator(this.regex)]);
			}
			const validator = this.validator || this.customValidator.bind(this);
			return new FormControl(input, validator);
		} else {
			return new FormControl(input);
		}
	}

	setCanShowError(canShowError: boolean) {
		this.showError = canShowError;
	}

	isShowError() {
		return this.showError;
	}

	setLoading(loading) {
		this.loading = loading;
	}

	isLoading() {
		return this.loading;
	}

	resetErrorMessage() {
		this.errorMessage = `${this.DEFAULT_ERROR_PREFIX} ${this.label}`;
	}

	setRequired(required) {
		this.required = required;
	}

	setShowInputStateSuffix(show) {
		this.showInputStateSuffix = show;
	}

	isShowInputStateSuffix() {
		return this.showInputStateSuffix;
	}

	setErrorMessage(errorMessage) {
		this.errorMessage = errorMessage;
	}

	getErrorMessage() {
		return this.errorMessage;
	}

	setInputType(type: InputType) {
		this.inputType = type;
	}

	getInputType() {
		return this.inputType;
	}

	getLabel() {
		return this.label;
	}

	setPlaceHolder(placeHolder) {
		this.placeHolder = placeHolder;
	}

	getPlaceHolder() {
		return this.placeHolder;
	}

	getFormGroupControl() {
		return this.formGroupControl;
	}

	setFormGroupControl(formGroupControl: FormControl) {
		this.formGroupControl = formGroupControl;
	}

	validate() {
		return this.required ? this.getValidationResult() : true;
	}

	getValidationResult() {
		return this.formGroupControl?.valid && this.isValidInputLength();
	}

	customValidator(control: AbstractControl): { [key: string]: any } | null {
		const input = control.value;
		const isValid = !StringHelper.isNullOrEmpty(input);
		if (isValid) {
			return null;
		} else {
			return {input: true};
		}
	}

	regexValidator = (regex: RegExp) => {
		return (control: AbstractControl) => {
			const input = control.value;
			const isValid = regex.test(input);
			if (isValid) {
				return null;
			} else {
				return {input: true};
			}
		};
	}

	getInput() {
		return this.input;
	}

	setInput(input) {
		try {
			this.input = input;
			this.formGroupControl.patchValue(input);
		} catch (err) {
			logger.error('Error patching form ', err);
		}
	}

	isRequired() {
		return this.required;
	}

	setMaxLength(length: number) {
		this.maxLength = length;
	}

	setMinLength(length: number) {
		this.minLength = length;
	}

	getMaxLength() {
		return this.maxLength;
	}

	getMinLength() {
		return this.minLength;
	}

  setReadOnlyText(readOnlyText: boolean) {
    this.readOnlyText = readOnlyText;
  }

  isReadOnlyText(){
    return this.readOnlyText;
  }

	setShowCharCount(value: boolean) {
		return (this.showCharCount = value);
	}

	private isValidInputLength() {
		let input = this.input?.toString();
		try {
			input = input?.trim();
		} catch (error) {
			logger.error('error', error);
		}
		const inputLength = input?.length;
		return inputLength >= this.minLength && inputLength <= this.maxLength;
	}
}

export class EmailInput extends CustomInput {
	constructor(input, label, required) {
		super(input, label, required);
	}

	customValidator(control: AbstractControl): { [key: string]: any } | null {
		const email = control.value;
		const isValid = StringHelper.validEmail(email);
		if (isValid) {
			return null;
		} else {
			return {email: true};
		}
	}
}

export class SelectInput extends CustomInput implements SelectOption {
	items: SelectAdaptor[];
	allowClear: boolean;
	showSearch: boolean;
	borderless = false;

	constructor(input, label, required, validator?:any) {
		super(input, label, required, validator);
		this.allowClear = true;
		this.showSearch = true;
	}
}

export interface SelectOption {
	items: SelectAdaptor[];
}

export interface SelectAdaptor {
	label: string;
	value: any;
	icon?: any;
}

export class PhoneNumberInput extends CustomInput implements CountryCode {
	countryCode: CountryPhoneNumberCode;
	readonly DEFAULT_PHONE_NO = 11;
	prefix: any;

	constructor(input, label, required, validator = null, regex?: RegExp) {
		super(input, label, required, validator, regex);
		this.setInputType(InputType.mobile_phone);
		this.countryCode = CountryPhoneNumberCode.NG;
		this.setMaxLength(this.DEFAULT_PHONE_NO);
	}

	customValidator(control: AbstractControl): { [key: string]: any } | null {
		const phoneNumber = control.value;
		const isValid = StringHelper.validNigeriaMobileNumber(phoneNumber);
		if (isValid) {
			return null;
		} else {
			return {phoneNumber: true};
		}
	}
}

export class CacInput extends CustomInput {
	readonly DEFAULT_CAC_LENGTH = 12;
	prefix: string;

	constructor(input: string, label: string, required: boolean, validator = null, regex?: RegExp) {
		super(input, label, required, validator, regex);
		this.setInputType(InputType.text);
		this.setMaxLength(this.DEFAULT_CAC_LENGTH);
	}

  customValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    const isValid = StringHelper.validCAC(value);
    if (isValid) {
      return null;
    } else {
      return {cac: true};
    }
  }

	setInput(input: string) {
		const prefixAndInput = `${this.prefix ?? ''}${input}`
		super.setInput(prefixAndInput)
	}
}

export enum CountryPhoneNumberCode {
	NG = 'NIGERIA',
}

export interface CountryCode {
	countryCode: CountryPhoneNumberCode;
}

export class BvnInput extends CustomInput {
	readonly DEFAULT_BVN_LENGTH = 11;
  readonly invalidNinPrefixes = ['07', '08', '09'];

	constructor(input, label, required) {
		super(input, label, required);
		this.setMinLength(this.DEFAULT_BVN_LENGTH);
		this.setMaxLength(this.DEFAULT_BVN_LENGTH);
		this.setInputType(InputType.number);
	}

	customValidator(control: AbstractControl): { [key: string]: any } | null {
    const inputLabel = this.getLabel();
    const isNinInput = inputLabel?.toLowerCase().includes('nin');
		const bvn = control.value;
    const isValidBvn = StringHelper.validBVN(bvn);
    const isValidNin = isValidBvn && !StringHelper.containsPrefix(bvn, this.invalidNinPrefixes);
    const isValid = isNinInput ? isValidNin : isValidBvn;
		if (isValid) {
			return null;
		} else {
			return {bvn: true};
		}
	}
}

export class UsernameInput extends CustomInput {
	constructor(input, label, required, validator?: any) {
		super(input, label, required, validator);
	}

	customValidator(control: AbstractControl): { [key: string]: any } | null {
		const username = control.value;
		const isValid = StringHelper.isValidUsername(username);
		if (isValid) {
			return null;
		} else {
			return {username: true};
		}
	}
}

export class PasswordInput extends CustomInput implements PasswordValidator {
	validatorType: PasswordValidatorType;
	passwordHint: string;
	showPasswordHint: boolean;

	constructor(input, label, required, passwordValidatorType: PasswordValidatorType = PasswordValidatorType.BASIC, maxLength: number = 0) {
		super(input, label, required);
		this.showPasswordHint = true;
		const max = maxLength || this.getMaxLength();
		this.validatorType = passwordValidatorType;
		this.setMaxLength(max);
		const params = {
			validatorType: this.validatorType,
			maxLength: max,
		};
		this.setInputType(InputType.password);
		this.getFormGroupControl().setValidators([passwordValidator(params)]);
	}

	static getValidators(password): Array<ValidatorModel> {
		return ValidatorsHelper.validatePassword(password);
	}

	static customPasswordValidation(password) {
		const passwordValidators: Array<ValidatorModel> = PasswordInput.getValidators(password);
		let isValid = true;
		passwordValidators.forEach((e) => {
			isValid = isValid && e.passed;
		});
		return isValid;
	}

	static pinValidation(password, maxLength): boolean {
		return StringHelper.validPin(password, maxLength);
	}
}

export function passwordValidator(params: any): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } | null => {
		const password: string = control.value;
		let validPassword: boolean;
		switch (params?.validatorType) {
			case PasswordValidatorType.BASIC:
				validPassword = StringHelper.validSecret(password);
				break;
			case PasswordValidatorType.CUSTOM:
				validPassword = PasswordInput.customPasswordValidation(password);
				break;
			default:
				validPassword = PasswordInput.pinValidation(password, params?.maxLength);
		}
		return validPassword ? null : {password: true};
	};
}

export enum PasswordValidatorType {
	BASIC,
	CUSTOM,
	PIN,
}

export interface FutureDate {
	canShowFutureDate: boolean;
}

export interface PasswordValidator {
	validatorType: PasswordValidatorType;

	passwordHint: string;

	showPasswordHint: boolean;
}

export class DateInput extends CustomInput implements FutureDate {
	canShowFutureDate: boolean;

	constructor(input, label, required, futureDate = false) {
		super(input, label, required);
		this.canShowFutureDate = futureDate;
		this.setInputType(InputType.date);
	}
}

export enum InputType {
	text,
	date,
	select,
	password,
	pin,
	mobile_phone,
	number,
  alphanumeric
}
